import { useState, useEffect } from 'react'
import API from '../../services/api.js'
import { useNavigate } from 'react-router-dom'
import { ChevronRight, AlertTriangle } from "lucide-react"

export default function GrcOverview() {
  const navigate = useNavigate()
  const [summary, setSummary] = useState({
    controls: { mapped: 0, partial: 0, gap: 0, not_applicable: 0 },
    pendingMappings: 0,
    recentActivity: [],
    frameworkCounts: [],
    expiringEvidence: [],
    expiredEvidence: []
  })
  const [loading, setLoading] = useState(true)
  const [profile, setProfile] = useState(null)
  const [showActivity, setShowActivity] = useState(false)

  // Page-level asset scope — same sessionStorage → /resolve-asset-id/
  // pattern used elsewhere. The dashboard's coverage-status counts come
  // from org_control_status, which is keyed on (tenant_id, asset_id,
  // control_id) — without this, the summary aggregates every asset in
  // the tenant together, which was the bug being fixed here.
  const selectedAppName = sessionStorage.getItem('selectedApp')
  const [assetId, setAssetId] = useState(null)
  const [resolvingAsset, setResolvingAsset] = useState(true)
  const [assetError, setAssetError] = useState(null)

  useEffect(() => {
    if (!selectedAppName) {
      setAssetError('No asset selected — navigate to this page via an asset.')
      setResolvingAsset(false)
      return
    }
    setResolvingAsset(true)
    API.get('/resolve-asset-id/', { params: { name: selectedAppName } })
      .then(({ data }) => {
        setAssetId(data.id)
        setAssetError(null)
      })
      .catch(() => {
        setAssetError('Could not resolve the selected asset. Please re-select it from the dashboard.')
      })
      .finally(() => setResolvingAsset(false))
  }, [selectedAppName])

  useEffect(() => {
    if (assetId) fetchSummary()
    else if (!resolvingAsset) setLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId])

  async function fetchSummary() {
    setLoading(true)
    try {
      const { data } = await API.get('/grc-summary/', { params: { asset_id: assetId } })
      setProfile(data.profile)
      setSummary({
        controls: data.controls,
        pendingMappings: data.pendingMappings,
        recentActivity: data.recentActivity || [],
        frameworkCounts: data.frameworkCounts || [],
        expiringEvidence: data.expiringEvidence || [],
        expiredEvidence: data.expiredEvidence || [],
      })
    } catch (err) {
      console.error('Failed to fetch GRC summary:', err?.response?.data?.detail || err.message)
    } finally {
      setLoading(false)
    }
  }

  const totalControls = Object.values(summary.controls).reduce((a, b) => a + b, 0)

  if (resolvingAsset || loading) {
    return (
      <div className="fixed inset-0 z-[9999] bg-black flex items-center justify-center overflow-hidden">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-white/10 border-t-blue-400 rounded-full animate-spin" />
          <p className="text-sm text-gray-400">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen relative overflow-x-hidden w-full">

      <div className="absolute inset-0 bg-cover bg-center" />
      <div className="absolute inset-0 bg-black/80" />

      <div className="relative z-10">
        <div className="relative w-full max-w-[1400px] mx-auto px-3 md:px-4 lg:px-6 py-6 sm:py-8 min-w-0">
          {/* Header */}
          <div className="flex flex-wrap items-start justify-between gap-4 mb-8 sm:mb-10">
            <div className="relative min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-semibold text-white tracking-tight relative">
                <span className="relative z-10">
                  Dashboard{profile?.email ? `, ${profile.email}` : ''}
                </span>
                <span className="absolute -inset-1 blur-xl opacity-20 text-blue-500">
                  Dashboard
                </span>
              </h1>
              <div
                className="
                  mt-2 h-[2px]
                  w-[180px]
                  md:w-[200px]
                  lg:w-[320px]
                  bg-gradient-to-r
                  from-blue-500/60
                  via-blue-400/10
                  to-transparent
                "
              />
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => setShowActivity(true)}
                className="
                  px-2.5 sm:px-4 py-2 rounded-lg text-[11px] sm:text-sm font-medium
                  text-gray-300 bg-white/5 border border-white/10
                  hover:bg-white/10 hover:border-white/20
                  active:bg-white/10 active:scale-[0.98]
                  transition-all duration-200 whitespace-nowrap
                "
              >
                Recent Mapping Activity
              </button>
            </div>
          </div>

          {assetError && (
            <div className="mb-6 flex items-start gap-2 px-4 py-3 rounded-lg bg-red-500/5 border border-red-500/20">
              <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
              <p className="text-sm text-red-400">{assetError}</p>
            </div>
          )}

          {/* Coverage status summary */}
          <div className="mb-8 sm:mb-10">
            <h2 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4">
              Coverage Status
              <span className="text-gray-500 font-normal text-sm sm:text-base ml-2">
                ({totalControls} Total Controls)
              </span>
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
              {[
                { label: 'Mapped',         key: 'mapped',         bg: 'bg-green-500/10',  text: 'text-green-400',  ring: 'border-green-500/20'  },
                { label: 'Partial',        key: 'partial',        bg: 'bg-yellow-500/10', text: 'text-yellow-400', ring: 'border-yellow-500/20' },
                { label: 'Gap',            key: 'gap',            bg: 'bg-red-500/10',    text: 'text-red-400',    ring: 'border-red-500/20'    },
                { label: 'Not Applicable', key: 'not_applicable', bg: 'bg-gray-500/10',   text: 'text-gray-400',   ring: 'border-gray-500/20'   }
              ].map(item => (
                <div
                  key={item.key}
                  className={`
                    relative overflow-hidden
                    ${item.bg} border ${item.ring}
                    rounded-xl p-3 sm:p-4
                    transition-all duration-200
                    hover:scale-[1.01]
                  `}
                >
                  <div className="absolute inset-0 opacity-60 pointer-events-none" />
                  <div className="relative">
                    <p className={`text-2xl sm:text-3xl font-semibold ${item.text}`}>
                      {summary.controls[item.key]}
                    </p>
                    <p className="text-xs sm:text-sm text-gray-300 mt-0.5">{item.label}</p>
                    {totalControls > 0 && (
                      <p className="text-xs text-gray-400 mt-1">
                        {Math.round((summary.controls[item.key] / totalControls) * 100)}%
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
            <div className="space-y-4 sm:space-y-6">

              {/* Navigation cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                {[
                  { title: 'Policies',       desc: 'Upload and manage policy documents',          path: '/grc/policies'       },
                  { title: 'Controls',       desc: 'Track coverage across all frameworks',        path: '/grc/controls'       },
                  { title: 'Questionnaires', desc: 'Create and answer compliance questionnaires', path: '/grc/questionnaires' },
                  { title: 'RCM Builder',    desc: 'Build and export Risk Control Matrices',      path: '/grc/rcm'            }
                ].map((item) => (
                  <button
                    key={item.title}
                    onClick={() => navigate(item.path)}
                    className="
                      group relative overflow-hidden
                      bg-white/10 backdrop-blur-xl
                      border border-white/20 rounded-xl
                      px-4 sm:px-5 py-4 sm:py-5
                      text-left w-full
                      shadow-[0_0_25px_rgba(255,255,255,0.05)]
                      transition-all duration-300
                      lg:bg-white/5
                      lg:border-white/10
                      lg:shadow-none
                      lg:hover:bg-white/10 lg:hover:border-white/20
                      lg:hover:shadow-[0_0_25px_rgba(255,255,255,0.05)]
                      active:bg-white/10 active:border-white/20
                      active:scale-[0.98]
                    "
                  >
                    <div className="
                      absolute inset-0 rounded-xl
                      opacity-100 lg:opacity-0 lg:group-hover:opacity-100
                      transition-opacity duration-300
                    ">
                      <div className="absolute inset-0 rounded-xl border border-white/10 shadow-[inset_0_0_20px_rgba(255,255,255,0.05)]" />
                    </div>
                    <div className="absolute inset-0 overflow-hidden rounded-xl">
                      <div className="
                        absolute -top-10 left-0 w-full h-20
                        bg-gradient-to-r from-transparent via-white/5 to-transparent
                        rotate-12
                        opacity-100 lg:opacity-0 lg:group-hover:opacity-100
                        transition-opacity duration-300
                      " />
                    </div>
                    <div className="
                      absolute top-0 right-0 w-10 h-10
                      bg-gradient-to-bl from-white/10 to-transparent
                      opacity-100 lg:opacity-0 lg:group-hover:opacity-100
                      transition-opacity duration-200
                    " />
                    <div className="relative z-10">
                      <h2 className="font-semibold text-white text-sm tracking-tight flex items-center gap-2">
                        {item.title}
                        <span className="
                          w-1.5 h-1.5 rounded-full
                          bg-white/40 lg:bg-white/20 lg:group-hover:bg-white/40
                          transition-colors duration-200
                        " />
                      </h2>
                      <p className="text-xs text-gray-400 leading-snug mt-1">{item.desc}</p>
                      <div className="
                        mt-3 h-px
                        w-14 lg:w-0 lg:group-hover:w-14
                        transition-all duration-300
                        bg-white/20
                      " />
                    </div>
                  </button>
                ))}
              </div>

              {/* Pending mappings alert */}
              {summary.pendingMappings > 0 && (
                <div className="
                  flex items-center justify-between gap-3
                  bg-white/5 border border-white/10
                  rounded-lg px-3 sm:px-4 py-3
                ">
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                    <span className="h-2 w-2 flex-shrink-0 rounded-full bg-yellow-400 animate-pulse" />
                    <div className="leading-tight min-w-0">
                      <p className="text-xs sm:text-sm text-yellow-400 font-medium truncate">
                        {summary.pendingMappings} Pending Mapping
                        {summary.pendingMappings !== 1 ? 's' : ''}
                      </p>
                      <p className="text-[10px] sm:text-[11px] text-gray-500">
                        Requires review before processing
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate('/grc/policies')}
                    className="
                      flex-shrink-0 text-xs font-medium
                      text-gray-300 hover:text-white
                      transition flex items-center gap-1
                    "
                  >
                    Review
                    <ChevronRight className="w-3 h-3 sm:w-3.5 sm:h-3.5 opacity-70" />
                  </button>
                </div>
              )}

              {/* Evidence alerts */}
              {(summary.expiredEvidence.length > 0 || summary.expiringEvidence.length > 0) && (
                <div className="space-y-3">
                  {/* Expired evidence */}
                  {summary.expiredEvidence.length > 0 && (
                    <div className="bg-white/5 border border-white/10 rounded-lg overflow-hidden">
                      <div className="flex items-center gap-3 px-3 sm:px-4 py-3 border-b border-white/10">
                        <span className="h-2 w-2 flex-shrink-0 rounded-full bg-red-400" />
                        <p className="text-xs sm:text-sm text-red-400 font-medium">
                          {summary.expiredEvidence.length} Expired Evidence Item
                          {summary.expiredEvidence.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                      <div className="divide-y divide-white/5">
                        {summary.expiredEvidence.map(item => (
                          <div
                            key={item.id}
                            onClick={() => navigate(`/grc/controls/${item.control_id}`)}
                            className="
                              flex items-start sm:items-center justify-between gap-3
                              px-3 sm:px-4 py-3
                              cursor-pointer transition-colors hover:bg-white/5
                            "
                          >
                            <div className="min-w-0 flex-1">
                              <p className="text-xs sm:text-sm text-red-300 truncate">{item.title}</p>
                              <p className="text-[10px] sm:text-xs text-gray-500 truncate">
                                {item.controls?.control_id} · {item.controls?.title}
                              </p>
                            </div>
                            <p className="text-[10px] sm:text-xs text-gray-500 flex-shrink-0">
                              Expired {new Date(item.expiry_date).toLocaleDateString()}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Expiring soon evidence */}
                  {summary.expiringEvidence.length > 0 && (
                    <div className="bg-white/5 border border-white/10 rounded-lg overflow-hidden">
                      <div className="flex items-center gap-3 px-3 sm:px-4 py-3 border-b border-white/10">
                        <span className="h-2 w-2 flex-shrink-0 rounded-full bg-yellow-400 animate-pulse" />
                        <p className="text-xs sm:text-sm text-yellow-400 font-medium">
                          {summary.expiringEvidence.length} Evidence Item
                          {summary.expiringEvidence.length !== 1 ? 's' : ''} Expiring Soon
                        </p>
                      </div>
                      <div className="divide-y divide-white/5">
                        {summary.expiringEvidence.map(item => (
                          <div
                            key={item.id}
                            onClick={() => navigate(`/grc/controls/${item.control_id}`)}
                            className="
                              flex items-start sm:items-center justify-between gap-3
                              px-3 sm:px-4 py-3
                              cursor-pointer transition-colors hover:bg-white/5
                            "
                          >
                            <div className="min-w-0 flex-1">
                              <p className="text-xs sm:text-sm text-yellow-300 truncate">{item.title}</p>
                              <p className="text-[10px] sm:text-xs text-gray-500 truncate">
                                {item.controls?.control_id} · {item.controls?.title}
                              </p>
                            </div>
                            <p className="text-[10px] sm:text-xs text-gray-500 flex-shrink-0">
                              Expires {new Date(item.expiry_date).toLocaleDateString()}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Framework breakdown */}
              {summary.frameworkCounts.length > 0 && (
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex items-end justify-between">
                    <h2 className="text-base sm:text-lg font-semibold text-white tracking-tight">
                      Controls per Framework
                    </h2>
                    <span className="text-xs text-gray-600">
                      {summary.frameworkCounts.length} Frameworks
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                    {summary.frameworkCounts.map(({ name, count }) => (
                      <div
                        key={name}
                        className="
                          group relative overflow-hidden
                          bg-white/5 border border-white/10
                          rounded-xl px-3 sm:p-4 py-3
                          flex justify-between items-center
                          transition-all duration-200
                          hover:bg-white/10 hover:border-white/20
                        "
                      >
                        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className="absolute -top-6 left-0 w-full h-14 bg-gradient-to-r from-transparent via-white/5 to-transparent rotate-12" />
                        </div>
                        <div className="relative min-w-0 flex-1 pr-4">
                          <p className="text-xs sm:text-sm text-white font-medium tracking-tight truncate">
                            {name}
                          </p>
                        </div>
                        <div className="relative text-right flex-shrink-0">
                          <p className="text-xs sm:text-sm text-gray-300 font-medium">{count}</p>
                          <p className="text-[10px] sm:text-[11px] text-gray-500">Controls</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Activity Modal */}
              {showActivity && (
                <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center px-0 sm:px-4">
                  <div
                    className="absolute inset-0 bg-black/70 animate-[fadeIn_0.2s_ease-out]"
                    onClick={() => setShowActivity(false)}
                  />
                  <div className="
                    relative w-full sm:w-[95%] sm:max-w-2xl
                    max-h-[85vh] sm:max-h-[80vh]
                    bg-black/90 border border-white/10
                    rounded-t-2xl sm:rounded-2xl
                    backdrop-blur-xl overflow-hidden shadow-2xl
                    animate-[slideUp_0.3s_cubic-bezier(0.32,0.72,0,1)]
                    sm:animate-[fadeIn_0.2s_ease-out]
                  ">
                    <div className="flex items-center justify-between px-4 sm:px-5 py-3 sm:py-4 border-b border-white/10">
                      <h2 className="text-white font-semibold text-sm tracking-tight">
                        Recent Mapping Activity
                      </h2>
                      <button
                        onClick={() => setShowActivity(false)}
                        className="text-gray-400 hover:text-white text-sm px-2 py-1 rounded-md hover:bg-white/5 transition"
                      >
                        ✕
                      </button>
                    </div>
                    <div className="max-h-[calc(85vh-52px)] sm:max-h-[calc(80vh-52px)] overflow-y-auto scrollbar-hide">
                      {summary.recentActivity.length === 0 ? (
                        <div className="p-6 text-sm text-gray-500">No activity found</div>
                      ) : (
                        <div className="divide-y divide-white/5">
                          {summary.recentActivity.map((activity) => {
                            const oldStatus = activity.old_data?.status
                            const newStatus = activity.new_data?.status
                            return (
                              <div key={activity.id} className="px-4 sm:px-5 py-3 hover:bg-white/5 transition group">
                                <p className="text-xs sm:text-sm text-gray-300">
                                  <span className="text-white font-medium">{activity.changedByName}</span>{' '}
                                  {activity.action === 'INSERT' && 'created a mapping'}
                                  {activity.action === 'UPDATE' && oldStatus && newStatus && (
                                    <>
                                      changed{' '}
                                      <span className="text-gray-400">{oldStatus.replace('_', ' ')}</span>
                                      {' → '}
                                      <span className={
                                        newStatus === 'accepted' ? 'text-green-400' :
                                        newStatus === 'rejected' ? 'text-red-400' :
                                        newStatus === 'edited'   ? 'text-blue-400' :
                                        'text-white'
                                      }>
                                        {newStatus.replace('_', ' ')}
                                      </span>
                                    </>
                                  )}
                                </p>
                                <p className="text-[10px] sm:text-xs text-gray-500 mt-1">
                                  {new Date(activity.changed_at).toLocaleString()}
                                </p>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

            </div>
        </div>
      </div>
    </div>
  )
}